import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { getSectionRoute } from '@services/routes.service';
import SectionsList from '@shopDetails/components/SectionsList';
import CapacityCheck from '@shopDetails/components/CapacityCheck';
import Tabs from '@components/Tabs';
import ShopHeader from '@shopDetails/components/ShopHeader/ShelvesMenuHeader';
import MenuShimmer from '@conversion/components/MenuShimmer';
import InfiniteScroll from '@components/InfiniteScroll';
import ShelvesLayout from '@conversion/containers/ShelvesLayout';
import Shimmer from '@shopDetails/components/Shimmer';
import { scrollTo, SCROLL_BEHAVIOR } from '@utils/scroll';
import { SHOP_TABS, SHOP_TAB_INDEX } from '@shopDetails/constants';
import { getWindowObject } from '@utils/documentWindow';
import ShopHeaderNotification from '@shopDetails/components/ShopHeader/components/Notification/Notification';
import connect from './connect';

const ShopDetailsContainer = styled(InfiniteScroll)`
  flex-grow: 1;
`;

const ShopDetailsTabs = styled(Tabs)`
  /* TODO: fallback for old browsers that don't support sticky */
  position: sticky;
  top: 100px;
  box-shadow: ${({ theme }) => theme.commons.header.elevation};
`;

class ShopDetailsShelves extends Component {
  state = {
    showSections: false,
    sectionSelected: null,
  };

  isMounted = false;

  tabHasChanged = false;

  componentDidMount() {
    this.isMounted = true;

    const {
      tabSelectedId,
      fetchMeasurementUnitsList,
      fetchCapacityCheck,
      capacityCheckActive,
      checkJokerCartItemsQuantity,
    } = this.props;
    checkJokerCartItemsQuantity();
    if (capacityCheckActive) fetchCapacityCheck();

    fetchMeasurementUnitsList();
    if (tabSelectedId !== SHOP_TAB_INDEX) {
      SHOP_TABS.map(m => {
        if (m.id === tabSelectedId) {
          this.setState({ showSections: true });
        }
        return false;
      });
    }
  }

  componentWillUnmount() {
    this.isMounted = false;
  }

  componentDidUpdate(prevProps, prevState) {
    const { sectionsList: currentSectionList } = this.props;
    const { sectionsList: prevSectionList } = prevProps;
    const { tabId: prevTabId } = prevState;
    const {
      tabId: currentTabId,
      [`scrollTop-${currentTabId}`]: scrollForCurrentTab,
    } = this.state;

    if (currentTabId !== prevTabId) {
      this.tabHasChanged = true;
    }

    this.shouldScroll({
      scrollForCurrentTab,
      prevTabId,
      currentTabId,
      currentSectionList,
      prevSectionList,
    });
  }

  shouldScroll = ({
    scrollForCurrentTab,
    prevTabId,
    currentTabId,
    currentSectionList,
    prevSectionList,
  }) => {
    if (scrollForCurrentTab && currentSectionList === prevSectionList) {
      if (prevTabId !== currentTabId) this.handleScroll(currentTabId);
      if (prevTabId === currentTabId && this.tabHasChanged) {
        this.tabHasChanged = false;
        this.handleScroll(currentTabId);
      }
    } else if (currentTabId !== prevTabId) {
      this.handleScroll();
    }
  };

  setStateIfMounted = newState => {
    if (this.isMounted) this.setState(newState);
  };

  fetchMoreShops = () => {
    const {
      shop,
      fetchMenu,
      isFetchingMenu,
      fetchSectionsList,
      isFetchingSectionList,
      sectionLoadStatus,
    } = this.props;
    const { showSections } = this.state;
    if (!isFetchingMenu && !showSections) {
      fetchMenu({ shop, isScrolling: true });
    }
    if (!isFetchingSectionList && showSections && sectionLoadStatus?.hasMore) {
      fetchSectionsList();
    }
  };

  PAGE_COUNTER = 2;

  SAVED_CURRENT_LENGTH = 0;

  shotTrackSectionListexpand = (prevlength, currentLength) => {
    const { trackSectionListExpanded } = this.props;
    const pagehaschanged = !!(
      currentLength.count > 0 &&
      prevlength &&
      prevlength.count !== currentLength.count &&
      currentLength.count !== this.SAVED_CURRENT_LENGTH.count
    );
    if (pagehaschanged) {
      trackSectionListExpanded(
        this.PAGE_COUNTER,
        this.SAVED_CURRENT_LENGTH.count,
      );
      this.PAGE_COUNTER += 1;
    }
    this.SAVED_CURRENT_LENGTH = currentLength;
  };

  getShelvesLayoutConfiguration = () => {
    const { shelvesLoadStatus, sectionLoadStatus } = this.props;
    const { showSections } = this.state;
    this.shotTrackSectionListexpand(shelvesLoadStatus, sectionLoadStatus);
    return {
      fetchMore: this.fetchMoreShops,
      hasMore: showSections
        ? sectionLoadStatus?.hasMore
        : shelvesLoadStatus?.hasMore || false,
      loader: shelvesLoadStatus?.hasMore && !showSections && (
        <MenuShimmer key="0" />
      ),
    };
  };

  openAgeValidation = sectionSelected => {
    const { openAgeValidation } = this.props;
    this.setState({
      sectionSelected: sectionSelected?.id ? sectionSelected : null,
    });
    openAgeValidation({ onValidate: this.onValidateAge });
  };

  onValidateAge = validated => {
    const { tabSelectedId } = this.props;
    const { sectionSelected } = this.state;

    if (
      validated &&
      sectionSelected &&
      (tabSelectedId === 1 || tabSelectedId > SHOP_TAB_INDEX)
    ) {
      this.onShelveExpand(sectionSelected);
    }
  };

  handleScroll = tabId => {
    const { SMOOTH, AUTO } = SCROLL_BEHAVIOR;
    const { [`scrollTop-${tabId}`]: top } = this.state;
    scrollTo({ top: top || 0, behavior: top ? SMOOTH : AUTO });
  };

  getScrollTop = () => {
    const { pageYOffset, document: doc } = getWindowObject() || {};
    return (
      (pageYOffset || doc?.documentElement?.scrollTop) -
      (doc?.documentElement?.clientTop || 0)
    );
  };

  onSelectTabs = tabId => {
    const { showSections, trackShopDetailsTabs, showCatalog } = this.props;
    const scrollTop = this.getScrollTop();

    if (tabId > SHOP_TAB_INDEX) {
      showSections();
      trackShopDetailsTabs();
    } else {
      showCatalog();
      trackShopDetailsTabs();
    }

    this.setState(prevState => {
      const tab = { 1: 2, 2: 1 }[tabId];
      const isSectionTabSelected = tabId > SHOP_TAB_INDEX;
      return {
        ...prevState,
        tabId,
        showSections: isSectionTabSelected,
        [`scrollTop-${tab}`]: scrollTop,
      };
    });
  };

  onShelveExpand = section => {
    const { pushPage, match } = this.props;
    const { city, restaurant } = match.params;
    const url = getSectionRoute({ city, restaurant, sectionId: section.id });
    pushPage(url);
    const { trackSectionDetailsLoaded } = this.props;
    trackSectionDetailsLoaded({
      sectionId: section?.id,
      sectionType: section?.customImage?.type,
    });
  };

  render() {
    const {
      menu,
      hasToValidateAge,
      onBeforeProductConfig,
      sectionsList,
      tabSelectedId,
      countryCode,
      onGoBack,
      showShimmer,
      isFetchingMenu,
      shop,
      subHeader,
      footer,
      capacityCheckActive,
    } = this.props;

    const { showSections } = this.state;

    const scrollConfig = this.getShelvesLayoutConfiguration();
    return (
      <>
        <ShopDetailsContainer
          fetchMore={scrollConfig.fetchMore}
          hasMore={scrollConfig.hasMore}
          loader={scrollConfig.loader}
          enabled
        >
          <ShopHeader goBack={onGoBack} subHeader={subHeader} shop={shop} />

          <>
            <ShopDetailsTabs
              tabs={SHOP_TABS}
              onSelect={this.onSelectTabs}
              tabSelectedId={tabSelectedId}
            />
            <ShopHeaderNotification shop={shop} />
            <SectionsList
              show={showSections}
              openAgeValidation={this.openAgeValidation}
              sectionsList={sectionsList}
              onShelveExpand={this.onShelveExpand}
              hasToValidateAge={hasToValidateAge}
            />
          </>

          {Boolean(capacityCheckActive) && <CapacityCheck />}
          {showShimmer && <Shimmer />}
          {!showSections && (
            <ShelvesLayout
              menu={menu}
              isFetchingMenu={isFetchingMenu}
              onBeforeProductConfig={onBeforeProductConfig}
              openAgeValidation={this.openAgeValidation}
              hasToValidateAge={hasToValidateAge}
              countryCode={countryCode}
            />
          )}
        </ShopDetailsContainer>
        {footer}
      </>
    );
  }
}

ShopDetailsShelves.propTypes = {
  // data
  tabSelectedId: PropTypes.number,
  isFetchingSectionList: PropTypes.bool.isRequired,
  isFetchingMenu: PropTypes.bool.isRequired,
  sectionsList: PropTypes.array.isRequired,
  capacityCheckActive: PropTypes.bool,
  shelvesLoadStatus: PropTypes.shape({
    count: PropTypes.number,
    hasMore: PropTypes.bool,
  }),
  sectionLoadStatus: PropTypes.shape({
    count: PropTypes.number,
    hasMore: PropTypes.bool,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      city: PropTypes.string,
      restaurant: PropTypes.string,
    }),
  }).isRequired,

  // WithShopDetails
  shop: PropTypes.object,
  menu: PropTypes.object,
  hasToValidateAge: PropTypes.bool.isRequired,
  countryCode: PropTypes.string.isRequired,
  showShimmer: PropTypes.bool.isRequired,
  onGoBack: PropTypes.func.isRequired,
  onBeforeProductConfig: PropTypes.func.isRequired,
  openAgeValidation: PropTypes.func.isRequired,
  subHeader: PropTypes.node,
  footer: PropTypes.node,

  // callbacks
  pushPage: PropTypes.func.isRequired,
  fetchMenu: PropTypes.func.isRequired,
  fetchMeasurementUnitsList: PropTypes.func.isRequired,
  showCatalog: PropTypes.func.isRequired,
  showSections: PropTypes.func.isRequired,
  trackShopDetailsTabs: PropTypes.func.isRequired,
  fetchSectionsList: PropTypes.func.isRequired,
  fetchCapacityCheck: PropTypes.func.isRequired,
  checkJokerCartItemsQuantity: PropTypes.func,
  trackSectionDetailsLoaded: PropTypes.func,
  trackSectionListExpanded: PropTypes.func,
};

ShopDetailsShelves.defaultProps = {
  checkJokerCartItemsQuantity: () => {},
};

export { ShopDetailsShelves as ShopDetailsTests };
export default connect(ShopDetailsShelves);
